import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { QRCodeCanvas } from "qrcode.react";
import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import images from "../../assets/images";
import TemplateLayout from "../../components/layouts/templatelayout";
import Loader from "../../components/loader";
import RoleSwitchSuccessModal from "../../components/modal/RoleSwitchSuccessModal";
import SwitchToBothRoleModal from "../../components/modal/SwitchToBothRoleModal";
import { useTranslate } from "../../context/TranslationContext";
import {
  useBuyProductMutation,
  useGetPurchasedProductQuery,
} from "../../redux/Apis/DigitalProducts";
import { useGetCurrentSubscriptionQuery } from "../../redux/Apis/Packages";
import { useGetTemplateByIdQuery } from "../../redux/Apis/Template";
import { useUpdateUserMutation } from "../../redux/Apis/User";
import { convertPrice, getConfigByCountryCode } from "../../utils/localization";
import "./style.css";

const getEmbedUrl = (url) => {
  if (!url) return null;
  const isPlaylist = url.includes("list=");
  const playlistMatch = url.match(/[?&]list=([a-zA-Z0-9_-]+)/);
  const videoMatch = url.match(/(?:v=|\.be\/|\/embed\/)([a-zA-Z0-9_-]{11})/);
  if (isPlaylist && playlistMatch) {
    return `https://www.youtube.com/embed/videoseries?list=${playlistMatch[1]}`;
  }
  if (videoMatch) {
    return `https://www.youtube.com/embed/${videoMatch[1]}`;
  }
  return null;
};

const TemplateDetail = () => {
  const { t } = useTranslate("templateDetail");
  const lang = useSelector((state) => state.language.lang);
  const navigate = useNavigate();
  const { isLoggedIn, user } = useSelector((state) => state.authSlice);
  const { slug } = useParams();
  const currentUrl = `${window.location.origin}/templates/${slug}`;
  const countryCode =
    user?.country || localStorage?.getItem("selectedCountry") || "gb";
  const [displayPrice, setDisplayPrice] = useState(null);
  const [showRoleSwitchModal, setShowRoleSwitchModal] = useState(false);
  const [showRoleSwitchSuccessModal, setShowRoleSwitchSuccessModal] =
    useState(false);

  const [updateUser, { isLoading: isLoadingUpdate }] = useUpdateUserMutation();

  const { data, isLoading, refetch } = useGetTemplateByIdQuery({
    id: slug,
    params: { lang },
  });
  const template = data?.data;

  const { data: current_subscription, isSuccess: currentSuccess } =
    useGetCurrentSubscriptionQuery({ lang }, { skip: !isLoggedIn });

  const { data: purchasedProductsRes, isSuccess: hasPurchasedData } =
    useGetPurchasedProductQuery({ params: { lang } }, { skip: !isLoggedIn });

  const purchasedProducts = purchasedProductsRes?.data || [];

  const isProductPurchased =
    hasPurchasedData &&
    purchasedProducts.some((item) => item?.product?._id === template?._id);

  const [
    buyDigitalProduct,
    { data: buyData, isSuccess: buySuccess, error: buyError },
  ] = useBuyProductMutation();

  useEffect(() => {
    async function updatePrice() {
      if (template?.pricing) {
        const baseAmount = template.pricing;
        const { currency, symbol } = getConfigByCountryCode(countryCode);
        const converted = await convertPrice(baseAmount, "GBP", currency);
        const numericValue = Number(converted);
        setDisplayPrice(`${symbol} ${numericValue.toFixed(2)}`);
      }
    }
    updatePrice();
  }, [template?.pricing, countryCode]);

  useEffect(() => {
    if (slug) refetch();
  }, [slug, refetch]);

  useEffect(() => {
    if (buySuccess && buyData?.data?.checkoutUrl) {
      window.location.href = buyData.data.checkoutUrl;
    }
  }, [buySuccess, buyData]);

  useEffect(() => {
    if (buyError) {
      toast.error(buyError?.data?.message || t("purchaseError"));
    }
  }, [buyError, t]);

  if (isLoading) return <Loader loading={true} />;

  if (!template) {
    return (
      <TemplateLayout>
        <section className="text-center py-5">
          <h3 className="text-danger">{t("notFound")}</h3>
        </section>
      </TemplateLayout>
    );
  }

  const handleSwitchRole = async () => {
    try {
      const payload = { role: "both" };

      await updateUser({
        id: user?._id,
        payload,
        params: { lang },
      }).unwrap();

      setShowRoleSwitchModal(false);
      setShowRoleSwitchSuccessModal(true);
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || t("roleUpdateError"));
    }
  };

  const handleAccessClick = () => {
    // const hasMembership =
    //   currentSuccess && current_subscription?.data?.status === "active";
    const formatMembershipName = (key) => {
      if (!key) return "";
      const words = key
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1));
      return words.join(" ");
    };

    // Validate if user has correct membership
    const checkMembershipAccess = (current_subscription, requiredKey) => {
      if (!current_subscription?.data) return { allowed: false };

      const userPackage = current_subscription.data.package;
      const userKey = userPackage.title?.toLowerCase().replace(/\s+/g, "_");
      const required = requiredKey?.toLowerCase().replace(/\s+/g, "_");

      const isActive =
        current_subscription.data.status === "active" &&
        current_subscription.data.cancel === false &&
        current_subscription.data.expiration === false;

      // Any active membership allowed
      if (required === "membership_only") {
        return { allowed: isActive };
      }

      // Specific membership required (fit_pro, fit_business)
      if (required !== userKey) {
        return {
          allowed: false,
          requiredReadable: formatMembershipName(required),
          currentReadable: userPackage.title,
        };
      }

      return { allowed: true };
    };

    if (!user?.role) {
      if (template.access_type === "free") {
        return toast.info(t("loginFree"));
      } else if (template.access_type === "membership") {
        return toast.info(t("loginMembership"));
      } else if (template.access_type === "paid") {
        return toast.info(t("loginPaid"));
      } else if (template.access_type === "custom") {
        return toast.info(t("loginCustom"));
      }
      return;
    }

    // if (user.role === "learner") {
    //   if (template.access_type === "free") {
    //     return toast.info(t("switchCreatorFree"));
    //   } else if (template.access_type === "membership") {
    //     return toast.info(t("switchCreatorMembership"));
    //   } else if (template.access_type === "paid") {
    //     return toast.info(t("switchCreatorPaid"));
    //   } else if (template.access_type === "custom") {
    //     return toast.info(t("switchCreatorCustom"));
    //   }
    //   return;
    // }

    if (user.role === "learner") {
      setShowRoleSwitchModal(true);
      return;
    }

    if (template.access_type === "free") {
      return navigate("/my-teaching", {
        state: {
          activeTab: "pills-pss",
          access_type: template.access_type,
          template_id: template._id,
          title: template.title,
          template_price: template.pricing,
        },
      });
    }

    if (template.access_type === "membership") {
      const hasMembershipActive =
        currentSuccess &&
        current_subscription?.data?.status === "active" &&
        current_subscription?.data?.cancel === false &&
        current_subscription?.data?.expiration === false;

      if (!hasMembershipActive) {
        return toast.info(t("membershipUpgradeNeeded"));
      }

      const result = checkMembershipAccess(
        current_subscription,
        data?.data?.membership_type,
      );

      if (!result.allowed) {
        const htmlMessage = t("membershipPlanNotAllowed")
          .replace("{{required}}", `<b>${result.requiredReadable}</b>`)
          .replace("{{current}}", `<b>${result.currentReadable}</b>`);

        return toast.info(
          <div dangerouslySetInnerHTML={{ __html: htmlMessage }} />,
        );
      }
      toast.info(t("upgradeMembership"));
      // if (hasMembership) {
      return navigate("/my-teaching", {
        state: {
          activeTab: "pills-pss",
          access_type: template.access_type,
          template_id: template._id,
          title: template.title,
          template_price: template.pricing,
        },
      });
      // }
    }

    if (template.access_type === "paid") {
      if (isProductPurchased) {
        toast.info(t("alreadyPurchased"));
        return;
        // OR if you want to redirect:
        // return navigate("/my-teaching", {
        //   state: {
        //     activeTab: "pills-pss",
        //     access_type: template.access_type,
        //     title: template.title,
        //     template_id: template._id,
        //   },
        // });
      }
      return buyDigitalProduct({ id: template._id, params: { lang } });
    }

    if (template.access_type === "custom") {
      return toast.success(t("customThanks"));
    }
  };

  const renderContent = (html) => {
    if (!html) return "";

    let content = html.trim();

    // Convert UL/OL to custom checklist
    content = content
      .replace(/<ul>/g, `<ul class="custom-check-list">`)
      .replace(/<ol>/g, `<ol class="custom-check-list">`);

    // Detect multiple <p> tags
    const pMatches = [...content.matchAll(/<p>(.*?)<\/p>/g)];

    if (pMatches.length > 1) {
      return `
      <ul class="custom-check-list">
        ${pMatches.map((match) => `<li>${match[1]}</li>`).join("")}
      </ul>
    `;
    }

    // Detect multiple <br> separated lines
    const plainText = content
      .replace(/<\/p>/g, "")
      .replace(/<p>/g, "")
      .split(/<br\s*\/?>/);

    const validLines = plainText.filter(
      (line) => line.replace(/&nbsp;/g, "").trim() !== "",
    );

    if (validLines.length > 1) {
      return `
      <ul class="custom-check-list">
        ${validLines.map((line) => `<li>${line.trim()}</li>`).join("")}
      </ul>
    `;
    }

    // Single paragraph/text → normal render
    return content;
  };

  const yoututbeUrl = getEmbedUrl(template?.youtube_link);

  return (
    <>
      <Helmet>
        <title>
          Template Information | Ready-to-Use Training Resources | Traineego
        </title>

        <meta
          name="description"
          content="Access professionally designed fitness templates, workout plans, coaching resources, and productivity tools."
        />

        <meta name="robots" content="index,follow" />
        <link rel="canonical" href="https://traineego.com/template" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Fitness Template",
            url: "https://traineego.com/template",
            description:
              "Access professionally designed fitness templates, workout plans, coaching resources, and productivity tools.",
            publisher: {
              "@type": "Organization",
              name: "Traineego",
              url: "https://traineego.com",
            },
          })}
        </script>
      </Helmet>
      <TemplateLayout>
        <section
          style={{
            backgroundColor: "#000",
            color: "#fff",
            padding: "60px 0",
            minHeight: "100vh",
          }}
        >
          <div className="container">
            {/* Header */}
            <div className="row align-items-stretch mb-5">
              {/* LEFT CONTENT */}
              <div className="col-md-6 mb-4 mb-md-0 d-flex">
                <div className="w-100">
                  <h1
                    className="fw-bold mb-3"
                    style={{
                      fontSize: "40px",
                      lineHeight: "1.2",
                    }}
                  >
                    {template.title_hook || template.title}
                  </h1>

                  <div
                    className="fs-5 mb-4 text-muted"
                    style={{
                      lineHeight: "1.7",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: template.subtitle || "",
                    }}
                  />

                  <div className="row g-3">
                    {/* PRODUCTION */}
                    <div className="col-lg-6 d-flex">
                      <div
                        className="w-100 p-3"
                        style={{
                          backgroundColor: "var(--grayDarkColor)",
                          borderRadius: "12px",
                          border: "1px solid #444444",
                        }}
                      >
                        <p
                          className="mb-3"
                          style={{
                            fontWeight: "600",
                            fontSize: "17px",
                          }}
                        >
                          {t("production")}
                        </p>

                        <div
                          className="text-muted"
                          style={{
                            lineHeight: "1.7",
                            fontSize: "15px",
                            wordBreak: "break-word",
                          }}
                          dangerouslySetInnerHTML={{
                            __html: template.prerequisites || "",
                          }}
                        />
                      </div>
                    </div>

                    {/* QUALIFICATIONS */}
                    <div className="col-lg-6 d-flex">
                      <div
                        className="w-100 p-3"
                        style={{
                          backgroundColor: "var(--grayDarkColor)",
                          borderRadius: "12px",
                          border: "1px solid #444444",
                        }}
                      >
                        <p
                          className="mb-3"
                          style={{
                            fontWeight: "600",
                            fontSize: "17px",
                          }}
                        >
                          {t("qualifications")}
                        </p>

                        <div
                          className="text-muted"
                          style={{
                            lineHeight: "1.7",
                            fontSize: "15px",
                            wordBreak: "break-word",
                          }}
                          dangerouslySetInnerHTML={{
                            __html: template.level || "",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT IMAGE */}
              <div className="col-md-6 d-flex">
                <div
                  className="w-100 h-100 overflow-hidden template-image-wrapper"
                  style={{
                    borderRadius: "12px",
                  }}
                >
                  <img
                    src={
                      template?.file?.startsWith("http")
                        ? template.file
                        : images.dummy.course
                    }
                    alt={template.title}
                    className="img-fluid w-100 h-100 template-hero-image"
                    style={{
                      objectFit: "cover",
                      minHeight: "100%",
                      borderRadius: "12px",
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="row justify-content-center align-items-stretch g-3 mb-5">
              {/* LEFT BUTTONS */}
              <div className="col-lg-3 col-md-4 d-flex">
                <div className="w-100 d-flex flex-column justify-content-center cta-stack">
                  <button
                    className="site-btn w-100"
                    onClick={() => {
                      const canvas = document.getElementById("template-qr");
                      const pngUrl = canvas
                        .toDataURL("image/png")
                        .replace("image/png", "image/octet-stream");

                      const downloadLink = document.createElement("a");
                      downloadLink.href = pngUrl;
                      downloadLink.download = `template-${slug}-qr.png`;

                      document.body.appendChild(downloadLink);
                      downloadLink.click();
                      document.body.removeChild(downloadLink);
                    }}
                  >
                    <FontAwesomeIcon icon="fa-solid fa-download" />{" "}
                    {t("downloadQR")}
                  </button>

                  <button
                    className="site-btn w-100 mt-3 text-wrap"
                    onClick={handleAccessClick}
                  >
                    <FontAwesomeIcon icon="fa-solid fa-rocket" />{" "}
                    {template.access_type === "paid"
                      ? isProductPurchased
                        ? t("active")
                        : `${t("buyBundle")} - ${displayPrice ?? <Spinner />}`
                      : template.access_type === "free"
                        ? t("startFree")
                        : template.access_type === "membership"
                          ? t("useMembership")
                          : template.access_type === "custom"
                            ? t("requestCustom")
                            : t("useTemplate")}
                  </button>
                  {template.supportingfiles && (
                    <button
                      className="site-btn w-100 mt-3 text-nowrap"
                      onClick={() => {
                        if (template?.supportingfiles) {
                          window.open(template.supportingfiles, "_blank");
                        }
                      }}
                    >
                      {t("downloadPDF")}
                    </button>
                  )}
                </div>
              </div>

              {/* QR */}
              <div className="col-lg-3 col-md-4 d-flex">
                <div
                  className="w-100 text-center py-3 d-flex flex-column justify-content-center align-items-center qr-card"
                  style={{
                    borderRadius: "10px",
                    border: "1px solid var(--primaryColor)",
                  }}
                >
                  <p className="small text-muted mb-3">{t("scanCamera")}</p>

                  <QRCodeCanvas
                    id="template-qr"
                    value={currentUrl}
                    size={180}
                    bgColor={"#ffffff"}
                    fgColor={"#000000"}
                    level={"H"}
                    includeMargin={true}
                    style={{ borderRadius: "10px" }}
                  />
                </div>
              </div>

              {/* VIDEO */}
              {yoututbeUrl && (
                <div className="col-lg-6 col-md-12 d-flex">
                  <div
                    className="video-container ratio ratio-16x9 w-100 video-card"
                    style={{
                      border: "1px solid var(--primaryColor)",
                      borderRadius: "10px",
                      overflow: "hidden",
                    }}
                  >
                    <iframe
                      src={yoututbeUrl}
                      title="Press Video"
                      style={{ borderRadius: "12px" }}
                      allow="accelerometer autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>
              )}
            </div>

            {/* Info Sections */}
            <div className="row g-4 mb-4">
              {/* CARD 1 */}
              <div className="col-lg-4 col-md-6">
                <div
                  className="p-4 h-100 info-card"
                  style={{
                    backgroundColor: "#17161b",
                    borderRadius: "14px",
                    border: "1px solid #2a2a2a",
                  }}
                >
                  <p className="fw-bold mb-3 fs-5 d-flex align-items-center gap-2">
                    <FontAwesomeIcon
                      icon="fa-solid fa-star"
                      color="var(--primaryColor)"
                    />
                    {t("creatorBenefits")}
                  </p>

                  <div
                    className="content-details"
                    dangerouslySetInnerHTML={{
                      __html: renderContent(template.creator_benefits),
                    }}
                  />
                </div>
              </div>

              {/* CARD 2 */}
              <div className="col-lg-4 col-md-6">
                <div
                  className="p-4 h-100 info-card"
                  style={{
                    backgroundColor: "#17161b",
                    borderRadius: "14px",
                    border: "1px solid #2a2a2a",
                  }}
                >
                  <p className="fw-bold mb-3 fs-5 d-flex gap-2">
                    <FontAwesomeIcon
                      icon="fa-solid fa-bullseye"
                      color="var(--primaryColor)"
                      className="mt-1"
                    />
                    {t("whyBuildTrack")}
                  </p>

                  <div
                    className="content-details"
                    dangerouslySetInnerHTML={{
                      __html: renderContent(template.motivation),
                    }}
                  />
                </div>
              </div>

              {/* CARD 3 */}
              <div className="col-lg-4 col-md-6">
                <div
                  className="p-4 h-100 info-card"
                  style={{
                    backgroundColor: "#17161b",
                    borderRadius: "14px",
                    border: "1px solid #2a2a2a",
                  }}
                >
                  <p className="fw-bold mb-3 fs-5 d-flex align-items-center gap-2">
                    <FontAwesomeIcon
                      icon="fa-solid fa-lightbulb"
                      color="var(--primaryColor)"
                    />
                    {t("problem")}
                  </p>

                  <div
                    className="content-details"
                    dangerouslySetInnerHTML={{
                      __html: renderContent(template.problem),
                    }}
                  />
                </div>
              </div>

              {/* CARD 4 */}
              <div className="col-lg-6 col-md-6">
                <div
                  className="p-4 h-100 info-card"
                  style={{
                    backgroundColor: "#17161b",
                    borderRadius: "14px",
                    border: "1px solid #2a2a2a",
                  }}
                >
                  <p className="fw-bold mb-3 fs-5 d-flex align-items-center gap-2">
                    <FontAwesomeIcon
                      icon="fa-solid fa-heart"
                      color="var(--primaryColor)"
                    />
                    {t("whyCreatorsLove")}
                  </p>

                  <div
                    className="content-details"
                    dangerouslySetInnerHTML={{
                      __html: renderContent(template.creator_template),
                    }}
                  />
                </div>
              </div>

              {/* CARD 5 */}
              <div className="col-lg-6 col-md-6">
                <div
                  className="p-4 h-100 info-card"
                  style={{
                    backgroundColor: "#17161b",
                    borderRadius: "14px",
                    border: "1px solid #2a2a2a",
                  }}
                >
                  <p className="fw-bold mb-3 fs-5 d-flex gap-2">
                    <FontAwesomeIcon
                      icon="fa-solid fa-rocket"
                      color="var(--primaryColor)"
                      className="mt-2"
                    />
                    {template.socialproof || t("usedBy")}
                  </p>

                  <div
                    className="content-details"
                    dangerouslySetInnerHTML={{
                      __html: renderContent(template.notes),
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Action Button */}
            <div className="row mb-5 justify-content-center">
              <div
                className="col-md-12"
                style={{ justifyItems: "center", width: "fit-content" }}
              >
                <button
                  className="site-btn w-100 mt-2"
                  onClick={handleAccessClick}
                >
                  <FontAwesomeIcon icon="fa-solid fa-rocket" />{" "}
                  {template.access_type === "paid"
                    ? isProductPurchased
                      ? t("active")
                      : `${t("buyBundle")} - ${displayPrice ?? <Spinner />}`
                    : template.access_type === "free"
                      ? t("startFree")
                      : template.access_type === "membership"
                        ? t("useMembership")
                        : template.access_type === "custom"
                          ? t("requestCustom")
                          : t("useTemplate")}
                </button>
              </div>
            </div>

            {/* Supporting Files */}
            {/* {template.supportingfiles && (
            <div className="row mb-5">
              <div className="col-md-12" style={{ justifyItems: "center" }}>
                <div
                  className="p-3 text-center"
                  style={{
                    backgroundColor: "#2b2b2b",
                    borderRadius: "10px",
                    width: "fit-content",
                  }}
                >
                  <h6 className="fw-bold mb-2">{t("supportingFiles")}</h6>
                  <a
                    href={template?.supportingfiles}
                    className="site-btn"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("downloadPDF")}
                  </a>
                </div>
              </div>
            </div>
          )} */}

            {/* CTA */}
            <div className="text-center">
              <p className="mt-3 text-muted" style={{ fontSize: "13px" }}>
                {t("needHelp")}{" "}
                <a href="/contact" className="text-light">
                  {t("contactUs")}
                </a>
              </p>
            </div>
          </div>
        </section>
        <SwitchToBothRoleModal
          show={showRoleSwitchModal}
          close={() => setShowRoleSwitchModal(false)}
          onConfirm={handleSwitchRole}
          isLoading={isLoadingUpdate}
          title={t("switchRoleModal.title")}
          subtitle={t("switchRoleModal.subtitle")}
          paragraph1={t("switchRoleModal.paragraph1")}
          paragraph2={t("switchRoleModal.paragraph2")}
          roleText={t("switchRoleModal.role")}
          yesText={t("switchRoleModal.yesBtn")}
          cancelText={t("switchRoleModal.cancelBtn")}
        />
        <RoleSwitchSuccessModal
          show={showRoleSwitchSuccessModal}
          close={() => setShowRoleSwitchSuccessModal(false)}
          title={t("roleSwitchConfirmation.title")}
          subtitle={t("roleSwitchConfirmation.subtitle")}
          continueText={t("roleSwitchConfirmation.continueBtn")}
        />
      </TemplateLayout>
    </>
  );
};

export default TemplateDetail;
